import React, { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import Button from '@ui/Button';
import ModalV2, { ModalV2Props } from '../../../Modal/ModalV2';
import { Input } from '../../../Input';
import RadioGroup from '../../../Radio/RadioGroup';
import RadioButton from '../../../Radio/RadioButton';
import OtherLanguagesOption from '../components/OtherLanguagesOption';
import {
  AZURE_VERSION_PLACEHOLDER,
  getStudioVersionFromModel,
  ProjectVersionOptions,
  useProjectVersionProps,
  VersionSubOption,
} from '../hooks/useProjectVersion';
import useProjectCreateForm, {
  OTHER_LANGUAGES_EMPTY_ERROR,
  PROJECT_NAME_EMPTY_ERROR,
  useProjectCreateFormProps,
} from '../hooks/useProjectCreateForm';
import usePrevious from '../../../../lib/usePrevious';
import isArrayEqual from '../../../../util/isArrayEqual';
import { useAppSelector } from '../../../../store/hooks';
import FoldersBreadcrumb from '../../../Folders/Navigation/FoldersBreadcrumb';
import { useAncestorFoldersQuery } from '../../../Folders/operation.generated';
import { getDriveTypeFromName } from '../../../Drives/utils';
import { StudioProjectVersion } from '../../../../../server/graphql/__generated.types';
import { ProjectCreationSource } from '../../../../services/eventTrackingServiceTypes';
import ScriptStudioOption from '../components/ScriptStudioOption';
import LegacyOption from '../components/LegacyOption';
import UniversalOption from '../components/UniversalOption';
import LegacyStudioRetirementNotice from '../components/LegacyStudioRetirementNotice';

export const MAX_FILENAME_CHARACTER_LIMIT = 200;

export const PROJECT_FOLDER_CREATE_LOCATION_ERROR =
  'There was an connection issue. Refresh the page and try again.';

interface ProjectCreateModalV2Props
  extends Pick<ModalV2Props, 'open' | 'onClose'>,
    useProjectVersionProps,
    Pick<useProjectCreateFormProps, 'onSuccess' | 'tab' | 'variantId'>,
    Pick<ModelOptionContentProps, 'isAdditionalLanguagesUpgrade'> {
  folderId?: string;
  driveId?: string;
  source: ProjectCreationSource;
  isTeamUser?: boolean;
}

const useStyles = makeStyles()(theme => ({
  container: {
    width: '820px',
  },
  singleOption: {
    width: `464px`,
  },
  panel: {
    height: '347px',
    padding: theme.spacing(4),
    overflowY: 'scroll',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.grey[500]} transparent`,
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[500],
      borderRadius: 6,
    },
    '&::-webkit-scrollbar-corner': {
      display: 'none',
    },
  },
  singleOptionPanel: {
    padding: theme.spacing(4, 5, 5, 5),
    overflowY: 'scroll',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.grey[500]} transparent`,
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[500],
      borderRadius: 6,
    },
    '&::-webkit-scrollbar-corner': {
      display: 'none',
    },
  },
  projectDetails: {
    paddingTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  modelsContainer: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  radioGroup: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  radioButtonRoot: {
    '&[data-hover="true"] > div': {
      '&:not(&[data-disabled="true"])': {
        backgroundColor: palette.gray_800,
      },
    },
  },
}));

interface ModelOptionContentProps {
  option: ProjectVersionOptions;
  checked: boolean;
  error?: boolean;
  isAdditionalLanguagesUpgrade?: boolean;
  onModelChange: ReturnType<typeof useProjectCreateForm>['onModelChange'];
  selectedStudioVersion?: StudioProjectVersion | string | null;
}

/** Hooks-safe wrapper: memoizes language option list for stable OtherLanguagesOption props. */
const LegacyGlobalLanguagesContent = ({
  option,
  error,
  checked,
  isAdditionalLanguagesUpgrade,
  onModelChange,
  selectedStudioVersion,
}: Omit<ModelOptionContentProps, 'optionCount'>) => {
  const langOptions = React.useMemo(
    () =>
      (option.options || []).filter(
        (o): o is VersionSubOption => (o as any).kind === 'version'
      ),
    [option.options]
  );

  const handleChange = React.useCallback(
    (selected: VersionSubOption) => onModelChange(option.value, selected.id),
    [onModelChange, option.value]
  );

  return (
    <OtherLanguagesOption
      options={langOptions}
      error={error}
      onChange={handleChange}
      disabled={option.disabled}
      active={checked}
      isUpgrade={isAdditionalLanguagesUpgrade}
      selectedStudioVersion={selectedStudioVersion}
    />
  );
};

const ModelOptionContent = ({
  option,
  error,
  checked,
  isAdditionalLanguagesUpgrade = false,
  onModelChange,
  selectedStudioVersion,
}: ModelOptionContentProps) => {
  switch (option.id) {
    case StudioProjectVersion.Universal: {
      return <UniversalOption disabled={option.disabled} active={checked} />;
    }
    case StudioProjectVersion.ScriptStudio: {
      return <ScriptStudioOption disabled={option.disabled} active={checked} />;
    }
    case StudioProjectVersion.Legacy:
      return <LegacyOption disabled={option.disabled} active={checked} />;
    default:
      if (option.variant === 'tertiary') {
        return (
          <LegacyGlobalLanguagesContent
            option={option}
            error={error}
            checked={checked}
            isAdditionalLanguagesUpgrade={isAdditionalLanguagesUpgrade}
            onModelChange={onModelChange}
            selectedStudioVersion={selectedStudioVersion}
          />
        );
      }
      return <LegacyOption disabled={option.disabled} active={checked} />;
  }
};

const ProjectCreateModalV2 = (props: ProjectCreateModalV2Props) => {
  const {
    userVersions,
    enabledVersions,
    open,
    isAdditionalLanguagesUpgrade,
    onClose,
    onSuccess,
    folderId,
    driveId,
    source,
    isTeamUser,
    tab,
    variantId,
  } = props;
  const { classes, cx } = useStyles();

  const drivesState = useAppSelector(appState => appState.drives);
  const { currentDrive, currentFolder } = drivesState;
  const driveType = currentDrive
    ? getDriveTypeFromName(currentDrive)
    : undefined;

  const {
    data: folderAncestorData,
    loading: folderAncestorsLoading,
    error: ancestorError,
  } = useAncestorFoldersQuery({
    skip: !folderId,
    variables: {
      inputs: {
        // @ts-ignore - Using "skip" here to make sure this query isnt called if folderId isnt defined
        folderId,
      },
    },
  });

  const ancestorItems = React.useMemo(() => {
    if (
      ancestorError ||
      !folderAncestorData ||
      !folderAncestorData?.ancestorFolders
    )
      return [];
    return [...folderAncestorData.ancestorFolders].sort((a, b) => {
      return a.sequence - b.sequence;
    });
  }, [folderAncestorData, ancestorError]);

  const folderPath: Array<string> = React.useMemo(() => {
    const result: string[] = [];
    ancestorItems.forEach(folder => result.push(folder.name));
    if (currentFolder) result.push(currentFolder.name);
    return result;
  }, [ancestorItems, currentFolder]);

  const errorMessage = useMemo(() => {
    let err: string | null = null;
    if (!currentDrive && isTeamUser) err = PROJECT_FOLDER_CREATE_LOCATION_ERROR;
    return err;
  }, [currentDrive, isTeamUser]);

  const {
    inputs,
    error,
    loading,
    currentModel,
    modelOptions,
    onModelChange,
    onChange,
    submit,
    reset,
  } = useProjectCreateForm({
    userVersions: userVersions || [],
    enabledVersions,
    isAdditionalLanguagesUpgrade,
    onSuccess,
    folderId,
    driveId,
    driveType,
    source,
    tab,
    variantId,
  });

  const handleInputChange = (
    field: keyof ReturnType<typeof useProjectCreateForm>['inputs'],
    value: string
  ) => {
    onChange({
      ...inputs,
      [field]: value,
    });
  };

  const handleStudioModelChange = React.useCallback(
    (index: number) => {
      const next = getStudioVersionFromModel({
        models: modelOptions,
        index,
        enabledVersions,
      });
      if (next === undefined) return;
      onModelChange(index, next as StudioProjectVersion);
    },
    [modelOptions, enabledVersions, onModelChange]
  );

  /**
   * If versions change, make sure to reset the project create form
   */
  const previousUserVersions = usePrevious(userVersions);
  const previousEnabledVersions = usePrevious(enabledVersions);
  useEffect(() => {
    const isEnabledVersionsUpdated =
      enabledVersions &&
      previousEnabledVersions &&
      !isArrayEqual(previousEnabledVersions, enabledVersions);

    const isUserVersionsUpdated =
      previousUserVersions && !isArrayEqual(previousUserVersions, userVersions);

    if (isEnabledVersionsUpdated || isUserVersionsUpdated) {
      reset({ userVersions, enabledVersions });
    }
  }, [
    enabledVersions,
    previousEnabledVersions,
    previousUserVersions,
    reset,
    userVersions,
  ]);

  const previousOpen = usePrevious(open);
  useEffect(() => {
    if (previousOpen && !open) {
      reset({ userVersions: userVersions || [], enabledVersions });
    }
  }, [open, previousOpen, reset, userVersions, enabledVersions]);

  const showWorkspaceLocation = isTeamUser && currentDrive;

  // note: this is how we plop the retirement notice between the Universal and Legacy options
  const showLegacyRetirementNotice = useMemo(
    () =>
      modelOptions.some(o => o.id === StudioProjectVersion.Universal) &&
      modelOptions.some(
        o =>
          o.id === StudioProjectVersion.Legacy ||
          o.id === AZURE_VERSION_PLACEHOLDER
      ),
    [modelOptions]
  );

  return (
    <ModalV2.Modal
      open={open}
      onClose={onClose}
      title="New project"
      ctaLabel="Create project"
      classes={{
        modalBody:
          modelOptions.length === 1 ? classes.singleOption : classes.container,
      }}
      loading={loading || folderAncestorsLoading}
      disabled={isTeamUser && !currentDrive}
      footer={
        <Box margin={4} display="flex" justifyContent="center">
          <Button
            variant="primary"
            onClick={submit}
            style={{ height: 40, paddingLeft: 32, paddingRight: 32 }}
            disabled={loading}
          >
            Create project
          </Button>
        </Box>
      }
    >
      {/* note we are using this array syntax to avoid touching modalV2 as conditionally rendering
      still reads react children count as 2 which puts us in a 2fr grid context */}
      {[
        <Box
          key="project-details"
          className={
            modelOptions.length === 1
              ? classes.singleOptionPanel
              : classes.panel
          }
        >
          {modelOptions.length !== 1 && (
            <Typography variant="pMd" bold>{`Project details`}</Typography>
          )}
          <Box className={classes.projectDetails}>
            <Input
              id="project-name"
              data-e2e="project-name"
              label={'Project name'}
              fullWidth
              placeholder="New project"
              value={inputs.name}
              onChange={e => handleInputChange('name', e.currentTarget.value)}
              error={
                error === PROJECT_NAME_EMPTY_ERROR
                  ? 'Project name is required'
                  : undefined
              }
              maxLength={MAX_FILENAME_CHARACTER_LIMIT}
            />
            <Input
              id="project-description"
              data-e2e="project-description"
              label={'Description (optional)'}
              fullWidth
              placeholder="Client name, project description, etc."
              value={inputs.clientName || ''}
              onChange={e =>
                handleInputChange('clientName', e.currentTarget.value)
              }
              maxLength={MAX_FILENAME_CHARACTER_LIMIT}
            />
            {showWorkspaceLocation && (
              <Typography variant="pSm" bold>
                Location
                <FoldersBreadcrumb
                  drive={currentDrive?.name}
                  path={folderPath}
                />
              </Typography>
            )}
            {errorMessage && (
              <Box>
                <Typography variant="pSm" color="error" role="alert">
                  {errorMessage}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>,
        modelOptions.length > 1 && (
          <Box key="studio-models" className={classes.panel}>
            <Box className={classes.modelsContainer}>
              <Typography id={'models-label'} variant="pMd" component="h2" bold>
                {'Choose your Studio experience'}
              </Typography>
              <RadioGroup
                data-e2e="models-radio-group"
                className={classes.radioGroup}
                items={modelOptions}
                onChange={handleStudioModelChange}
                aria-labelledby={'models-label'}
              >
                {modelOptions.flatMap(o => {
                  const checked = currentModel === o.value;
                  const radio = (
                    <RadioButton
                      key={String(o.id)}
                      className={classes.radioButtonRoot}
                      checked={checked}
                      {...o}
                      onChange={() => handleStudioModelChange(o.value)}
                      disabled={o.disabled}
                    >
                      <ModelOptionContent
                        option={o}
                        checked={checked}
                        onModelChange={onModelChange}
                        error={error === OTHER_LANGUAGES_EMPTY_ERROR}
                        isAdditionalLanguagesUpgrade={
                          isAdditionalLanguagesUpgrade
                        }
                        selectedStudioVersion={inputs.studioVersion}
                      />
                    </RadioButton>
                  );
                  if (
                    o.id === StudioProjectVersion.Universal &&
                    showLegacyRetirementNotice
                  ) {
                    return [
                      radio,
                      <LegacyStudioRetirementNotice
                        key={`${String(o.id)}-legacy-retirement-notice`}
                      />,
                    ];
                  }
                  return [radio];
                })}
              </RadioGroup>
            </Box>
          </Box>
        ),
      ].filter(Boolean)}
    </ModalV2.Modal>
  );
};

export default ProjectCreateModalV2;
